// SHARED TEXTURE HELPERS — colour arithmetic and the deterministic hash every generator draws its jitter from.
//
// Split out of TextureEngine.js so texture-materials.js (the procedural material library) and TextureEngine.js (the
// cache + dispatch that hands a baked canvas to a material) can both use them without either importing the other.
// Nothing here touches THREE or the DOM.

// color1 may be EITHER an "rgb(r, g, b)" string (what lightenColor/darkenColor return) or a "#rrggbb" hex (what a
// caller holding a kube's own colour has). It used to be rgb() only, parsed with /\d+/g — and on a hex that quietly
// produced garbage rather than failing: "#505050" yields ONE digit group, not three, so match[1] and match[2] are
// undefined, parseInt(undefined) is NaN, and the result is the invalid CSS string "rgb(202125, NaN, NaN)". Canvas
// REJECTS an invalid fillStyle and silently keeps the previous one, so every rectangle painted with it came out in
// whatever colour was already set. Magnetite passed a hex for BOTH of its secondaries and so drew 110 rectangles in
// its own base colour — a perfectly uniform canvas, measured at standard deviation 0.00, i.e. a texture that existed
// and was flat. Iron and Copper each lost half their rectangles the same way.
export function blendColors(color1, color2, ratio) {
    const c1 = String(color1 || '');
    let r1, g1, b1;
    if (c1[0] === '#') {
        const n = parseInt(c1.slice(1), 16);
        r1 = (n >> 16) & 255; g1 = (n >> 8) & 255; b1 = n & 255;
    } else {
        const m = c1.match(/\d+/g) || [0, 0, 0];
        r1 = parseInt(m[0]) || 0; g1 = parseInt(m[1]) || 0; b1 = parseInt(m[2]) || 0;
    }
    const r = Math.floor(r1 * (1 - ratio) + parseInt(color2.substring(1, 3), 16) * ratio);
    const g = Math.floor(g1 * (1 - ratio) + parseInt(color2.substring(3, 5), 16) * ratio);
    const b = Math.floor(b1 * (1 - ratio) + parseInt(color2.substring(5, 7), 16) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
}

export function lightenColor(hex, amount = 0.3) {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.min(255, Math.floor((num >> 16) + amount * 255));
    const g = Math.min(255, Math.floor(((num >> 8) & 0x00FF) + amount * 255));
    const b = Math.min(255, Math.floor((num & 0x0000FF) + amount * 255));
    return `rgb(${r}, ${g}, ${b})`;
}

export function darkenColor(hex, amount = 0.3) {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.max(0, Math.floor((num >> 16) - amount * 255));
    const g = Math.max(0, Math.floor(((num >> 8) & 0x00FF) - amount * 255));
    const b = Math.max(0, Math.floor((num & 0x0000FF) - amount * 255));
    return `rgb(${r}, ${g}, ${b})`;
}

// Every generator draws its jitter/shade/phase out of hash01(), keyed off a seed derived from the kube's colour
// (hexSeed) or its name (nameSeed) — same name+hex in, same canvas out, on every client, every session. The metal
// lattices once rolled Math.random() at draw time on the theory that nobody compares them, but a player does: Iron
// came out with a different rust pattern every session, and two players on one Iron seam saw two different blocks.
// Nothing in the texture code calls Math.random now, and tests/material-looks.test.mjs holds it to that.
// hash01 is integer-only on purpose: Math.imul, ^, >>> and the /2^32 are all bit-exact by spec, so "the same canvas"
// is a guarantee even for a shade that lands on 0.49999999, whereas a sin-based hash (the Mineral and Regolith
// stipples used one until they were redrawn) can only promise it to about 10^-6, because an engine may approximate
// Math.sin. Inputs are truncated to int32 — every caller below passes integers.
export const hash01 = (x, y) => {
    let h = Math.imul((x | 0) + 0x9e3779b9, 0x85ebca6b) ^ Math.imul((y | 0) + 0x7f4a7c15, 0xc2b2ae35);
    h = Math.imul(h ^ (h >>> 15), 0x27d4eb2f);
    h ^= h >>> 13;
    h = Math.imul(h, 0x165667b1);
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};
export const hexSeed = (hex) => {
    let h = 0;
    for (let i = 0; i < hex.length; i++) h = (h * 31 + hex.charCodeAt(i)) % 9973;
    return h;
};

// A seed from a material's NAME, for the generators whose texture has to be a pure function of the material rather
// than of whichever kube happened to reach its cache slot first. hexSeed above folds everything into 0..9972, which
// is enough to tell one colour from another but packs the seeds so close that two generators drawing hash01(i + seed)
// can land on overlapping runs (seed 101's feature i is seed 100's feature i + 1). This takes the full 32 bits of
// FNV-1a, the same string hash makeRng (system-engine.js) opens with, and the drawing still goes through hash01 — so
// this library keeps exactly one source of jitter, and it is still integer-only and bit-exact on every engine.
export const nameSeed = (name) => {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < name.length; i++) h = Math.imul(h ^ name.charCodeAt(i), 16777619);
    return h | 0;
};
